import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Trophy, Medal, Award, Loader2, Crown } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rankBadge = (rank) => {
  if (rank === 1) return { Icon: Crown, color: 'text-yellow-500', bg: 'bg-yellow-50', border: 'border-yellow-200' };
  if (rank === 2) return { Icon: Medal, color: 'text-gray-400', bg: 'bg-gray-50', border: 'border-gray-200' };
  if (rank === 3) return { Icon: Award, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' };
  return { Icon: Trophy, color: 'text-stone-400', bg: 'bg-white', border: 'border-stone-200' };
};

const placeSuffix = (n) => {
  if (!n) return '-';
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  return `${n}th`;
};

const Leaderboard = ({ leaderboard, testIdPrefix = 'standings' }) => {
  if (!leaderboard || leaderboard.length === 0) {
    return (
      <Card className="border-stone-200">
        <CardContent className="py-12 text-center">
          <Trophy className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No results in this group yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-leaderboard`}>
      {/* Top 3 Podium */}
      {leaderboard.length >= 1 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {leaderboard.slice(0, 3).map(entry => {
            const { Icon, color, bg, border } = rankBadge(entry.rank);
            return (
              <Card
                key={entry.rider_name}
                className={`${border} ${bg} border-2`}
                data-testid={`${testIdPrefix}-podium-${entry.rank}`}
              >
                <CardContent className="p-6 text-center">
                  <Icon className={`w-12 h-12 ${color} mx-auto mb-3`} />
                  <div className="text-sm text-stone-500 font-medium">#{entry.rank}</div>
                  <div className="font-display text-xl font-bold text-stone-900 mt-1 break-words">
                    {entry.rider_name}
                  </div>
                  <div className="text-3xl font-bold text-primary mt-2">{entry.total_points}</div>
                  <div className="text-xs text-stone-500 mt-1">points</div>
                  <div className="text-xs text-stone-500 mt-2">
                    {entry.event_count} event{entry.event_count !== 1 ? 's' : ''}
                    {entry.first_place_count > 0 && ` · ${entry.first_place_count}× 1st`}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Full Table */}
      <Card className="border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left p-4 font-semibold text-stone-700 w-16">Rank</th>
                <th className="text-left p-4 font-semibold text-stone-700">Rider</th>
                <th className="text-right p-4 font-semibold text-stone-700">Points</th>
                <th className="text-right p-4 font-semibold text-stone-700 hidden sm:table-cell">Events</th>
                <th className="text-right p-4 font-semibold text-stone-700 hidden sm:table-cell">1st Places</th>
                <th className="text-right p-4 font-semibold text-stone-700 hidden md:table-cell">Best Finish</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map(entry => {
                const { Icon, color } = rankBadge(entry.rank);
                return (
                  <tr
                    key={entry.rider_name}
                    className="border-b border-stone-100 hover:bg-stone-50"
                    data-testid={`${testIdPrefix}-row-${entry.rank}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-5 h-5 ${color}`} />
                        <span className="font-bold">{entry.rank}</span>
                      </div>
                    </td>
                    <td className="p-4 font-medium text-stone-900">{entry.rider_name}</td>
                    <td className="p-4 text-right font-mono font-bold text-primary">{entry.total_points}</td>
                    <td className="p-4 text-right font-mono hidden sm:table-cell">{entry.event_count}</td>
                    <td className="p-4 text-right font-mono hidden sm:table-cell">{entry.first_place_count}</td>
                    <td className="p-4 text-right font-mono hidden md:table-cell">{placeSuffix(entry.best_place)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

const StandingsPage = () => {
  const [data, setData] = useState({ leaderboard: [], age_groups: [], years: [], selected_year: 'all' });
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState('all');

  useEffect(() => {
    const fetchStandings = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/standings`, { params: { year } });
        setData(res.data);
      } catch (err) {
        console.error('Failed to load standings', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStandings();
  }, [year]);

  const hasAgeGroups = data.age_groups && data.age_groups.length > 0;
  const totalRiders = data.leaderboard ? data.leaderboard.length : 0;

  return (
    <Layout>
      <SEO
        title="Season Standings"
        description="Season-long standings leaderboard for Manitoulin Horse Enthusiasts Club riders, grouped by age category."
        url="/standings"
      />

      {/* Header */}
      <div className="bg-primary text-white py-12">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-4xl font-bold mb-2">Season Standings</h1>
            <p className="text-white/80">Cumulative points earned across all events, grouped by age category</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-white/80 text-sm">Season:</span>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger
                className="bg-white text-stone-900 w-40"
                data-testid="standings-year-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All-Time</SelectItem>
                {data.years.map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : totalRiders === 0 ? (
          <Card className="border-stone-200">
            <CardContent className="py-16 text-center">
              <Trophy className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-stone-700 mb-2">No Standings Yet</h2>
              <p className="text-stone-500">Standings will appear once event results have been entered.</p>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="overall" className="w-full">
            <TabsList
              className="w-full justify-start bg-stone-100 p-1 h-auto flex-wrap"
              data-testid="standings-tabs"
            >
              <TabsTrigger
                value="overall"
                className="data-[state=active]:bg-white data-[state=active]:text-primary"
                data-testid="standings-tab-overall"
              >
                Overall
              </TabsTrigger>
              {hasAgeGroups && data.age_groups.map(group => (
                <TabsTrigger
                  key={group.age_category}
                  value={group.age_category}
                  className="data-[state=active]:bg-white data-[state=active]:text-primary"
                  data-testid={`standings-tab-${group.age_category}`}
                >
                  {group.age_category}
                  <span className="ml-2 text-xs text-stone-400">({group.leaderboard.length})</span>
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="overall" className="mt-6">
              <Leaderboard leaderboard={data.leaderboard} testIdPrefix="standings-overall" />
            </TabsContent>

            {hasAgeGroups && data.age_groups.map(group => (
              <TabsContent key={group.age_category} value={group.age_category} className="mt-6">
                <h2 className="font-display text-2xl font-semibold text-stone-900 mb-4">
                  {group.age_category}
                </h2>
                <Leaderboard
                  leaderboard={group.leaderboard}
                  testIdPrefix={`standings-${group.age_category}`}
                />
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
    </Layout>
  );
};

export default StandingsPage;
